import { useQuery, type QueryKey } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { fetchMyWorkspaces, getWorkspaceFromAccessToken } from '../lib/workspaces';

const DEFAULT_QUERY_KEY: QueryKey = ['user_workspaces'];

export type UseFederatedLoginGuardOptions = {
  accessToken: string | undefined;
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  signOut: () => Promise<void>;
  /** subscription-manager base URL — each app supplies its own configured value. */
  baseUrl: string;
  /** Query key to fetch and cache workspaces under. Defaults to ['user_workspaces']. */
  queryKey?: QueryKey;
};

/**
 * CLOUD-727. Business Cloud can revoke Federated Log In for a workspace after
 * an app session started. This revalidates the current workspace at load and
 * on window focus, and signs out once a successful response marks it disabled.
 * Fails open: an error, a pending query, or a missing row never signs out.
 */
export function useFederatedLoginGuard({
  accessToken,
  isAuthenticated,
  isAuthLoading,
  signOut,
  baseUrl,
  queryKey = DEFAULT_QUERY_KEY,
}: UseFederatedLoginGuardOptions): { isRevoking: boolean } {
  const workspaceId = getWorkspaceFromAccessToken(accessToken);
  const [isRevoking, setIsRevoking] = useState(false);
  const logoutStarted = useRef(false);

  const access = useQuery({
    queryKey,
    queryFn: () => fetchMyWorkspaces(baseUrl),
    enabled: isAuthenticated && !isAuthLoading && workspaceId !== null,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!access.isSuccess || !workspaceId || logoutStarted.current) return;
    const currentWorkspace = access.data.find((workspace) => workspace.id === workspaceId);
    if (currentWorkspace?.federatedLoginDisabled !== true) return;

    logoutStarted.current = true;
    setIsRevoking(true);
    signOut().catch((error) => {
      console.error('[useFederatedLoginGuard] signOut failed, will retry on next check', error);
      logoutStarted.current = false;
      setIsRevoking(false);
    });
    // dataUpdatedAt: react-query reuses the old data reference for a
    // structurally-equal refetch, so a retry after a failed sign-out
    // needs a dependency that changes on every fetch.
  }, [access.data, access.dataUpdatedAt, access.isSuccess, signOut, workspaceId]);

  return { isRevoking };
}
